<template>
  <div class="wework-page">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/" class="wework-breadcrumb">
      <el-breadcrumb-item :to="{ path: '/home' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>运维管理</el-breadcrumb-item>
      <el-breadcrumb-item>供应链采集监控</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 主内容区 -->
    <el-card class="wework-card" shadow="hover">
      <!-- 工具栏 -->
      <div class="tab-toolbar">
        <span class="toolbar-label">选择月份：</span>
        <el-date-picker
          v-model="currentMonth"
          type="month"
          placeholder="选择月份"
          size="small"
          style="width: 160px"
          value-format="yyyy-MM"
          :clearable="false"
          @change="loadAll"
        ></el-date-picker>
        <el-button size="small" style="margin-left: 12px" @click="changeMonth(-1)">上月</el-button>
        <el-button size="small" @click="changeMonth(1)">下月</el-button>
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadAll" style="margin-left: 8px">全部刷新</el-button>

        <!-- 状态图例 -->
        <div class="legend">
          <span class="legend-item"><i class="legend-dot dot-success"></i>采集成功</span>
          <span class="legend-item"><i class="legend-dot dot-partial"></i>部分失败</span>
          <span class="legend-item"><i class="legend-dot dot-failed"></i>采集失败</span>
          <span class="legend-item"><i class="legend-dot dot-none"></i>未覆盖</span>
        </div>
      </div>

      <!-- 日历网格 -->
      <div class="calendar-grid" v-loading="anyLoading">
        <div v-for="item in taskList" :key="item.key" class="calendar-item">
          <!-- 日历标题 -->
          <div class="calendar-title">
            <span>{{ item.label }}</span>
            <el-button
              type="text"
              size="mini"
              icon="el-icon-refresh"
              :loading="calendarData[item.key].loading"
              @click="refreshCalendar(item.key)"
            ></el-button>
          </div>

          <!-- 日历主体 -->
          <el-calendar v-model="calendarDate" class="monitor-calendar">
            <template slot="dateCell" slot-scope="{ data }">
              <div
                v-if="data.type === 'current-month'"
                :class="dayClass(item.key, data)"
                :title="dayTitle(item.key, data)"
                @click="handleDayClick(item.key, data)"
              >
                <span class="day-number">{{ getDayNumber(data.day) }}</span>
                <span v-if="getCell(item.key, data)" class="cell-count">
                  {{ getCell(item.key, data).count }}
                </span>
                <span v-else class="cell-hint">去补采</span>
              </div>
              <div v-else class="calendar-day other-month">
                <span class="day-number">{{ getDayNumber(data.day) }}</span>
              </div>
            </template>
          </el-calendar>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
// 覆盖状态文案（与后端 scm_collect_logs.status 一致）
const STATUS_LABEL = {
  success: '采集成功',
  partial: '部分失败',
  failed: '采集失败'
}

export default {
  name: 'ScmDataMonitor',
  data () {
    return {
      // 月份选择器值，格式 "yyyy-MM"，默认当前月
      selectedMonth: (() => {
        const now = new Date()
        const m = now.getMonth() + 1
        return `${now.getFullYear()}-${m < 10 ? '0' + m : m}`
      })(),
      // 4 个采集任务各自独立的数据（days 值为 { status, count }）
      calendarData: {
        scm_meituan_delivery_orders: { loading: false, days: {} },
        scm_meituan_delivery_details: { loading: false, days: {} },
        scm_shiheng_sale_orders: { loading: false, days: {} },
        scm_shiheng_sale_details: { loading: false, days: {} }
      },
      // 采集任务配置（taskKey 与后端 scm_collect_logs.enum 一致）
      taskList: [
        { key: 'scm_meituan_delivery_orders', label: '美团配送单单头' },
        { key: 'scm_meituan_delivery_details', label: '美团配送单明细' },
        { key: 'scm_shiheng_sale_orders', label: '食亨销售单单头' },
        { key: 'scm_shiheng_sale_details', label: '食亨销售单明细' }
      ]
    }
  },
  computed: {
    // 月份选择器的值（yyyy-MM 字符串）
    currentMonth: {
      get () {
        return this.selectedMonth
      },
      set (val) {
        this.selectedMonth = val
      }
    },
    // el-calendar 的 v-model 需要一个 Date 对象
    calendarDate: {
      get () {
        const [y, m] = this.currentMonth.split('-')
        return new Date(parseInt(y), parseInt(m) - 1, 1)
      },
      set () {
        // 留空，防止日历内部点击切换月份导致跳变
        // 月份只能通过顶部的 el-date-picker 切换
      }
    },
    // 是否有任意日历在加载
    anyLoading () {
      return Object.values(this.calendarData).some(item => item.loading)
    }
  },
  created () {
    // 请求令牌，防止快速切换月份时旧响应覆盖新数据
    this._loadTokens = {}
    this.loadAll()
  },
  methods: {
    // 获取年和月
    getYearAndMonth () {
      const [year, month] = this.currentMonth.split('-')
      return { year, month }
    },
    // 加载单个采集任务
    async loadCalendar (taskKey) {
      // 递增令牌，丢弃过期响应
      const token = (this._loadTokens[taskKey] || 0) + 1
      this._loadTokens[taskKey] = token
      this.calendarData[taskKey].loading = true
      try {
        const { year, month } = this.getYearAndMonth()
        const { data: res } = await this.$http.get('yunwei/scmDataMonitor/calendar', {
          params: { year, month, taskKey }
        })
        // 若期间又发起了新请求，则丢弃当前过期响应
        if (token !== this._loadTokens[taskKey]) return
        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg)
          return
        }
        this.calendarData[taskKey].days = res.data.days || {}
      } catch (err) {
        if (token !== this._loadTokens[taskKey]) return
        this.$message.error('加载数据失败')
      } finally {
        if (token === this._loadTokens[taskKey]) {
          this.calendarData[taskKey].loading = false
        }
      }
    },
    // 全部加载
    loadAll () {
      this.taskList.forEach(item => {
        this.loadCalendar(item.key)
      })
    },
    // 单独刷新某个日历
    refreshCalendar (taskKey) {
      this.loadCalendar(taskKey)
    },
    // 获取某天某任务的格子数据（null=未覆盖；status 空值＝库里有单据但无采集记录，同样视同未覆盖，可点击补采）
    getCell (taskKey, data) {
      const cell = this.calendarData[taskKey].days[data.day]
      return cell && cell.status ? cell : null
    },
    // 格子样式：已覆盖按状态上色，未覆盖灰色
    dayClass (taskKey, data) {
      const cell = this.getCell(taskKey, data)
      return ['calendar-day', cell ? 'status-' + cell.status : 'no-data']
    },
    // 格子 hover 提示
    dayTitle (taskKey, data) {
      const cell = this.getCell(taskKey, data)
      if (!cell) return '点击前往补采'
      const label = STATUS_LABEL[cell.status] || '无采集记录'
      return `${label} · 单据 ${cell.count} 条`
    },
    // 从 "2026-06-15" 提取日号
    getDayNumber (day) {
      return parseInt(day.split('-')[2])
    },
    // 上月/下月快捷切换
    changeMonth (delta) {
      const [y, m] = this.selectedMonth.split('-').map(Number)
      const d = new Date(y, m - 1 + delta, 1)
      const yy = d.getFullYear()
      const mm = d.getMonth() + 1
      this.selectedMonth = `${yy}-${mm < 10 ? '0' + mm : mm}`
      this.loadAll()
    },
    // 点击未覆盖日期，跳转采集日志页补采
    handleDayClick (taskKey, data) {
      if (this.getCell(taskKey, data)) return
      this.$router.push({
        path: '/yunwei/scmCollectLog',
        query: { taskKey, date: data.day }
      })
    }
  }
}
</script>

<style scoped>
.tab-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 0 12px;
}

.toolbar-label {
  font-size: 14px;
  color: #606266;
  margin-right: 4px;
}

/* 状态图例（靠右） */
.legend {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 14px;
}

.legend-item {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
}

.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 4px;
  box-sizing: border-box;
}

.dot-success {
  background: #e8f5e9;
  border: 1px solid #67c23a;
}

.dot-partial {
  background: #fdf6ec;
  border: 1px solid #e6a23c;
}

.dot-failed {
  background: #fef0f0;
  border: 1px solid #f56c6c;
}

.dot-none {
  background: #f5f7fa;
  border: 1px solid #c0c4cc;
}

.calendar-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;
}

.calendar-item {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
}

.calendar-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f5f7fa;
  font-weight: bold;
  font-size: 14px;
  color: #303133;
}

/* 隐藏 el-calendar 自带的标题栏 */
.monitor-calendar >>> .el-calendar__header {
  display: none;
}

/* 缩小日历单元格高度 */
.monitor-calendar >>> .el-calendar-table .el-calendar-day {
  min-height: 46px;
  height: 46px;
  padding: 2px;
}

/* 隐藏日历表头中的"今"按钮 */
.monitor-calendar >>> .el-calendar__body {
  padding: 0 8px 8px;
}

/* 日期单元格内容 */
.calendar-day {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.day-number {
  font-size: 13px;
  line-height: 1;
}

/* 已覆盖 - 按采集状态上色 */
.calendar-day.status-success {
  background: #e8f5e9;
}

.calendar-day.status-success .day-number {
  font-weight: bold;
  color: #67c23a;
}

.calendar-day.status-partial {
  background: #fdf6ec;
}

.calendar-day.status-partial .day-number {
  font-weight: bold;
  color: #e6a23c;
}

.calendar-day.status-failed {
  background: #fef0f0;
}

.calendar-day.status-failed .day-number {
  font-weight: bold;
  color: #f56c6c;
}

.cell-count {
  font-size: 11px;
  color: #909399;
  font-weight: bold;
  line-height: 1;
  margin-top: 2px;
}

.status-success .cell-count {
  color: #67c23a;
}

.status-partial .cell-count {
  color: #e6a23c;
}

.status-failed .cell-count {
  color: #f56c6c;
}

/* 相邻月日期淡化 */
.other-month {
  opacity: 0.3;
}

/* 未覆盖日期 - 可点击 */
.calendar-day.no-data {
  cursor: pointer;
  color: #c0c4cc;
}

.calendar-day.no-data:hover {
  background: #f0f7ff;
  color: #409eff;
}

.cell-hint {
  font-size: 10px;
  color: #c0c4cc;
  line-height: 1;
  margin-top: 2px;
}

.calendar-day.no-data:hover .cell-hint {
  color: #409eff;
}
</style>
